import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Block,
  BlockDes,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
  Row,
  Col,
} from "../../../components/Component";
import { useHistory } from "react-router-dom";
import { Input } from "reactstrap";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import moment from "moment";
import AdminSideDrawer from "../../../components/admin/AdminSideDrawer";
import {
  TC_ADMIN_PENDING_STALE_DAYS,
  buildClinicMapByPartner,
  derivePartnerTcStatus as resolvePartnerTcStatus,
  selectPartnersPendingTooLong,
  selectPaidSoonWithoutPractitioner,
  platformApprovedActiveTcClinicsCount,
  getTeleconsultationStatusBadgeTone,
  getTeleconsultationStatusDisplayLabel,
  isClinicListedForTeleconsultationPlatform,
} from "../../../utils/teleconsultationAdminMetrics";
import TeleconsultationPageShell from "../../../components/admin/TeleconsultationPageShell";

const TeleconsultationList = () => {
  const history = useHistory();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);

  const [allClinics, setAllClinics] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [tcEnabled, setTcEnabled] = useState(true);
  const [savingTcEnabled, setSavingTcEnabled] = useState(false);
  const [sortBy, setSortBy] = useState("scheduledAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const [consultationsTotal, setConsultationsTotal] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [drawerConsultation, setDrawerConsultation] = useState(null);
  const [clinicsWithNoPractitioner, setClinicsWithNoPractitioner] = useState([]);

  const fetchConsultations = useCallback((options = {}) => {
    const { signal } = options;
    setLoading(true);
    setError("");
    http
      .get("/api/v1/consultations", {
        params: {
          limit: 500,
          sortBy,
          sortOrder,
          includeArchived: includeArchived ? "true" : undefined,
        },
        signal,
      })
      .then((res) => {
        const list = res.data?.data || res.data?.consultations || res.data || [];
        setConsultations(Array.isArray(list) ? list : []);
        const t = res.data?.total;
        setConsultationsTotal(typeof t === "number" ? t : Array.isArray(list) ? list.length : null);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setError(e.response?.data?.message || "Failed to load consultations");
        setConsultations([]);
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, [sortBy, sortOrder, includeArchived]);

  const fetchTcSettingsAndData = useCallback((options = {}) => {
    const { signal } = options;
    http
      .get("/api/v1/settings", { signal })
      .then((res) => {
        const list = res.data?.data || [];
        const tcSetting = Array.isArray(list)
          ? list.find((s) => s?.key === "teleconsultation_enabled")
          : null;
        if (!tcSetting) {
          setTcEnabled(true);
          return;
        }
        const v = tcSetting.value;
        setTcEnabled(v === true || v === "true" || v === 1 || v === "1");
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setTcEnabled(true);
      });

    http
      .get("/api/v1/partners/medical/clinics", { signal })
      .then((res) => {
        const list = res.data?.data || res.data?.clinics || res.data || [];
        setAllClinics(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setAllClinics([]);
      });

    http
      .get("/api/v1/medical-partners", { signal })
      .then((res) => {
        const list = res.data?.data || res.data || [];
        setAllPartners(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setAllPartners([]);
      });
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchConsultations({ signal: ac.signal });
    fetchTcSettingsAndData({ signal: ac.signal });
    return () => ac.abort();
  }, [fetchConsultations, fetchTcSettingsAndData]);

  const tcClinics = useMemo(
    () => (Array.isArray(allClinics) ? allClinics.filter((c) => c?.offersTeleconsultation === true) : []),
    [allClinics]
  );

  const clinicMapByPartner = useMemo(() => buildClinicMapByPartner(tcClinics), [tcClinics]);

  const partnersWithTcStatus = useMemo(() => {
    const list = Array.isArray(allPartners) ? allPartners : [];
    return list.map((p) => ({
      ...p,
      tcStatusResolved: resolvePartnerTcStatus(p, clinicMapByPartner),
      tcClinics: clinicMapByPartner.get(String(p?._id || "")) || [],
    }));
  }, [allPartners, clinicMapByPartner]);

  const pendingPartnerRequests = useMemo(
    () => partnersWithTcStatus.filter((p) => p.tcStatusResolved === "pending"),
    [partnersWithTcStatus]
  );

  const approvedPartners = useMemo(
    () => partnersWithTcStatus.filter((p) => p.tcStatusResolved === "approved"),
    [partnersWithTcStatus]
  );

  const platformApprovedTcClinicsCount = useMemo(
    () => platformApprovedActiveTcClinicsCount(tcClinics),
    [tcClinics]
  );

  const partnersPendingTooLong = useMemo(
    () => selectPartnersPendingTooLong(pendingPartnerRequests),
    [pendingPartnerRequests]
  );

  const paidSoonWithoutPractitioner = useMemo(
    () => selectPaidSoonWithoutPractitioner(consultations),
    [consultations]
  );

  useEffect(() => {
    const targets = tcClinics.filter(
      (c) =>
        String(c?.status || "").toLowerCase() === "active" &&
        isClinicListedForTeleconsultationPlatform(c) &&
        c?.offersTeleconsultation === true
    );
    if (targets.length === 0) {
      setClinicsWithNoPractitioner([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const empty = [];
      await Promise.all(
        targets.map(async (c) => {
          try {
            const res = await http.get(`/api/v1/partners/medical/clinics/${c._id}/practitioners`);
            const arr = res.data?.data || [];
            const doctors = arr.filter(
              (p) => !["nurse", "assistant"].includes(String(p?.role || "").toLowerCase())
            );
            if (doctors.length === 0) empty.push({ _id: c._id, name: c.name || "Clinic" });
          } catch {
            /* ignore */
          }
        })
      );
      if (!cancelled) setClinicsWithNoPractitioner(empty);
    })();
    return () => {
      cancelled = true;
    };
  }, [tcClinics]);

  const saveGlobalTcEnabled = async (nextValue) => {
    setSavingTcEnabled(true);
    try {
      await http.put("/api/v1/settings/bulk", {
        settings: [
          {
            key: "teleconsultation_enabled",
            value: nextValue,
            label: "Teleconsultation globally enabled",
            description: "Enable/disable teleconsultation flow globally",
            category: "feature",
            type: "boolean",
            isPublic: true,
          },
        ],
      });
      setTcEnabled(nextValue);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to save teleconsultation global setting");
    } finally {
      setSavingTcEnabled(false);
    }
  };

  const toggleArchiveConsultation = async (item, nextArchived) => {
    const id = item._id || item.id;
    if (!id) return;
    setArchivingId(id);
    setError("");
    try {
      await http.put(`/api/v1/consultations/${id}`, { adminArchived: nextArchived });
      fetchConsultations();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to update archive state");
    } finally {
      setArchivingId(null);
    }
  };

  const filteredConsultations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return consultations;
    return consultations.filter((item) => {
      const p = item.patient || item.client;
      const email = typeof p === "object" && p != null ? p.email : "";
      if (email && String(email).toLowerCase().includes(q)) return true;
      const clinicName =
        typeof item.clinic === "object" && item.clinic != null ? String(item.clinic.name || "") : "";
      if (clinicName.toLowerCase().includes(q)) return true;
      const idStr = String(item._id || item.id || "");
      if (idStr.toLowerCase().includes(q)) return true;
      if (idStr.slice(-6).toLowerCase().includes(q)) return true;
      if (item.patientDisplay && String(item.patientDisplay).toLowerCase().includes(q)) return true;
      return false;
    });
  }, [consultations, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = filteredConsultations.slice(indexOfFirstItem, indexOfLastItem);
  const totalItems = filteredConsultations.length;

  const getStatusBadgeClass = (status) => getTeleconsultationStatusBadgeTone(status);

  const formatClient = (c) => {
    if (!c) return "—";
    if (typeof c === "object") {
      const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
      return c.email || name || c.name || c.firstName || c._id || "—";
    }
    return String(c);
  };

  const formatClinic = (clinic) => {
    if (!clinic) return "—";
    if (typeof clinic === "object") return clinic.name || clinic._id || "—";
    return String(clinic);
  };

  const formatSpecialty = (s) => {
    if (s == null) return "—";
    if (typeof s === "object" && s.label) return String(s.label);
    return String(s);
  };

  return (
    <>
      <AdminSideDrawer
        open={!!drawerConsultation}
        onClose={() => setDrawerConsultation(null)}
        title={
          drawerConsultation
            ? `Consultation ${String(drawerConsultation._id || drawerConsultation.id || "").slice(-8)}`
            : ""
        }
        subtitle={
          drawerConsultation
            ? `${formatClinic(drawerConsultation.clinic)} · ${drawerConsultation.patientDisplay || formatClient(drawerConsultation.patient || drawerConsultation.client)}`
            : ""
        }
        footer={
          drawerConsultation ? (
            <>
              <Button
                size="sm"
                color="primary"
                onClick={() => {
                  const cid = drawerConsultation._id || drawerConsultation.id;
                  if (cid) history.push(getPublicUrl(`teleconsultations/${cid}`));
                }}
              >
                Open full page
              </Button>
              <Button size="sm" color="light" type="button" onClick={() => setDrawerConsultation(null)}>
                Close
              </Button>
            </>
          ) : null
        }
      >
        {drawerConsultation ? (
          <div className="d-flex flex-column flex-g-2">
            <p className="mb-1">
              <span className="text-soft">Status:</span>{" "}
              {getTeleconsultationStatusDisplayLabel(drawerConsultation.status)}
            </p>
            <p className="mb-1">
              <span className="text-soft">Payment:</span> {drawerConsultation.paymentStatus || "—"}
            </p>
            <p className="mb-1">
              <span className="text-soft">Scheduled (UTC):</span>{" "}
              {drawerConsultation.scheduledAt
                ? moment.utc(drawerConsultation.scheduledAt).format("DD/MM/YYYY HH:mm")
                : "—"}
            </p>
            <p className="mb-1">
              <span className="text-soft">Practitioner:</span>{" "}
              {drawerConsultation.practitionerDisplayName || formatClient(drawerConsultation.practitioner) || "—"}
            </p>
            <p className="mb-1">
              <span className="text-soft">Amount:</span>{" "}
              {drawerConsultation.amount != null
                ? `${drawerConsultation.amount} ${drawerConsultation.currency || ""}`.trim()
                : "—"}
            </p>
            {drawerConsultation.videoRoomUrl ? (
              <p className="mb-0">
                <a href={drawerConsultation.videoRoomUrl} target="_blank" rel="noopener noreferrer" className="link">
                  Video room
                </a>
              </p>
            ) : null}
          </div>
        ) : null}
      </AdminSideDrawer>
      <TeleconsultationPageShell
        headTitle="Teleconsultations"
        title="Teleconsultations"
        description={
          <BlockDes className="text-soft">
            {loading
              ? "Loading..."
              : `Page ${currentPage}: rows ${totalItems === 0 ? 0 : indexOfFirstItem + 1}–${Math.min(
                  indexOfLastItem,
                  totalItems
                )} of ${totalItems} shown${searchQuery.trim() ? " (search)" : ""}${
                  consultationsTotal != null ? ` · ${consultationsTotal} loaded from API` : ""
                }.`}
          </BlockDes>
        }
      >
        <Block>
          <div className="admin-list-filters">
            <div className="admin-list-filters__field" style={{ flex: "1 1 280px", maxWidth: 480 }}>
              <label className="form-label small text-soft" htmlFor="tc-list-search">
                Search consultations
              </label>
              <Input
                id="tc-list-search"
                type="search"
                bsSize="sm"
                placeholder="Patient email, clinic name, or consultation ID…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>
          <Row className="g-3 mb-3">
            <Col md="6" lg="3">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Global teleconsultation</h6>
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="small text-soft">{tcEnabled ? "Enabled" : "Disabled"}</span>
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="tcGlobalEnabled"
                        checked={tcEnabled}
                        disabled={savingTcEnabled}
                        onChange={(e) => saveGlobalTcEnabled(e.target.checked)}
                      />
                      <label className="custom-control-label" htmlFor="tcGlobalEnabled">
                        Toggle
                      </label>
                    </div>
                  </div>
                  <p className="small text-soft mt-2 mb-0">Controls patient teleconsultation visibility globally.</p>
                </div>
              </div>
            </Col>
            <Col md="6" lg="4">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Pending partner requests</h6>
                  <h4 className="mb-0">{pendingPartnerRequests.length}</h4>
                  <p className="small text-soft mt-2 mb-0">Partner-level TC requests waiting for admin decision.</p>
                </div>
              </div>
            </Col>
            <Col md="6" lg="3">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Approved partners (TC)</h6>
                  <h4 className="mb-0">{approvedPartners.length}</h4>
                  <p className="small text-soft mt-2 mb-0">Approval status is now independent from clinic statuses.</p>
                </div>
              </div>
            </Col>
            <Col md="6" lg="3">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Active TC clinics (platform-approved)</h6>
                  <h4 className="mb-0">{platformApprovedTcClinicsCount}</h4>
                  <p className="small text-soft mt-2 mb-0">
                    Clinics that offer TC, are active, and have platform TC approval enabled.
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          <div className="card card-bordered mb-3">
            <div className="card-inner">
              <div className="d-flex flex-wrap justify-content-between align-items-center flex-g-2 mb-2">
                <h6 className="mb-0">TC business alerts</h6>
                <Button size="sm" color="primary" outline onClick={() => history.push(getPublicUrl("teleconsultations/operations"))}>
                  Open TC operations dashboard
                </Button>
              </div>
              <Row className="g-2">
                <Col md="4">
                  <div className={`border rounded p-2 small ${clinicsWithNoPractitioner.length ? "border-warning" : ""}`}>
                    <strong className="d-block">Clinics without practitioner</strong>
                    <span className="fs-5">{clinicsWithNoPractitioner.length}</span>
                    <p className="text-soft mb-0 mt-1">Active, platform TC–approved, offering TC.</p>
                  </div>
                </Col>
                <Col md="4">
                  <div className={`border rounded p-2 small ${paidSoonWithoutPractitioner.length ? "border-warning" : ""}`}>
                    <strong className="d-block">Paid (24h) — no practitioner</strong>
                    <span className="fs-5">{paidSoonWithoutPractitioner.length}</span>
                    <p className="text-soft mb-0 mt-1">Next 24 hours, paid, unassigned.</p>
                  </div>
                </Col>
                <Col md="4">
                  <div className={`border rounded p-2 small ${partnersPendingTooLong.length ? "border-warning" : ""}`}>
                    <strong className="d-block">Pending partners ({TC_ADMIN_PENDING_STALE_DAYS}+ days)</strong>
                    <span className="fs-5">{partnersPendingTooLong.length}</span>
                    <p className="text-soft mb-0 mt-1">Stale TC approval queue.</p>
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          {error && <div className="alert alert-danger" role="alert">{error}</div>}

          <Row className="g-2 align-items-end mb-3">
            <Col xs="12" md="auto">
              <label className="form-label mb-0 small text-soft">Sort by</label>
              <select
                className="form-control form-control-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort consultations by"
              >
                <option value="scheduledAt">Scheduled date</option>
                <option value="createdAt">Created date</option>
              </select>
            </Col>
            <Col xs="12" md="auto">
              <label className="form-label mb-0 small text-soft">Order</label>
              <select
                className="form-control form-control-sm"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                aria-label="Sort order"
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>
            </Col>
            <Col xs="12" md="auto" className="d-flex align-items-center pt-3 pt-md-4">
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="includeArchivedTc"
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                />
                <label className="custom-control-label small" htmlFor="includeArchivedTc">
                  Show archived (admin list)
                </label>
              </div>
            </Col>
            <Col xs="12" md="auto">
              <Button size="sm" color="light" onClick={() => fetchConsultations()}>
                Apply
              </Button>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <DataTable className="card-stretch" scrollable>
              <DataTableBody compact>
                <DataTableHead>
                  <DataTableRow size="sm"><span className="sub-text">ID / Date</span></DataTableRow>
                  <DataTableRow size="md"><span className="sub-text">Clinic</span></DataTableRow>
                  <DataTableRow size="md"><span className="sub-text">Client</span></DataTableRow>
                  <DataTableRow size="sm"><span className="sub-text">Practitioner</span></DataTableRow>
                  <DataTableRow size="sm"><span className="sub-text">Specialty</span></DataTableRow>
                  <DataTableRow size="sm"><span className="sub-text">Amount</span></DataTableRow>
                  <DataTableRow size="sm"><span className="sub-text">Scheduled (UTC)</span></DataTableRow>
                  <DataTableRow size="sm"><span className="sub-text">Video room</span></DataTableRow>
                  <DataTableRow size="sm"><span className="sub-text">Status / Pay</span></DataTableRow>
                  <DataTableRow className="nk-tb-col-tools text-right"><span className="sub-text">Actions</span></DataTableRow>
                </DataTableHead>
                {currentItems.length === 0 ? (
                  <DataTableItem>
                    <DataTableRow colSpan={10} className="text-center text-soft">No consultations found.</DataTableRow>
                  </DataTableItem>
                ) : (
                  currentItems.map((item) => (
                    <DataTableItem key={item._id || item.id}>
                      <DataTableRow size="sm" dataLabel="ID / Date">
                        <span className="tb-lead">{(item._id || item.id || "").toString().slice(-6)}</span>
                        <span className="sub-text d-block">{item.createdAt ? moment(item.createdAt).format("DD/MM/YY") : "—"}</span>
                      </DataTableRow>
                      <DataTableRow size="md" dataLabel="Clinic">{formatClinic(item.clinic)}</DataTableRow>
                      <DataTableRow size="md" dataLabel="Client">{item.patientDisplay || formatClient(item.patient || item.client)}</DataTableRow>
                      <DataTableRow size="sm" dataLabel="Practitioner">{item.practitionerDisplayName || formatClient(item.practitioner) || "—"}</DataTableRow>
                      <DataTableRow size="sm" dataLabel="Specialty">{formatSpecialty(item.specialty)}</DataTableRow>
                      <DataTableRow size="sm" dataLabel="Amount">
                        {item.amount != null ? `${item.amount} ${item.currency || ""}`.trim() : "—"}
                      </DataTableRow>
                      <DataTableRow size="sm" dataLabel="Scheduled">{item.scheduledAt ? moment.utc(item.scheduledAt).format("DD/MM/YY HH:mm") + " UTC" : "—"}</DataTableRow>
                      <DataTableRow size="sm" dataLabel="Video room">
                        {item.videoRoomUrl ? (
                          <a href={item.videoRoomUrl} target="_blank" rel="noopener noreferrer" className="link link-sm">
                            Open
                          </a>
                        ) : (
                          <span className="text-soft small">—</span>
                        )}
                      </DataTableRow>
                      <DataTableRow size="sm" dataLabel="Status / Pay">
                        <span className={`badge badge-dot badge-${getStatusBadgeClass(item.status)}`}>
                          {getTeleconsultationStatusDisplayLabel(item.status)}
                        </span>
                        {item.adminArchived ? (
                          <span className="badge badge-dim bg-secondary ms-1">Archived</span>
                        ) : null}
                        <span className="sub-text d-block small">{item.paymentStatus || item.status || "—"}</span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools text-right" dataLabel="Actions">
                        <Button
                          color="primary"
                          size="sm"
                          className="btn-icon"
                          onClick={() => setDrawerConsultation(item)}
                          title="Quick view"
                        >
                          <Icon name="eye" />
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          className="ms-1"
                          disabled={archivingId === (item._id || item.id)}
                          onClick={() => toggleArchiveConsultation(item, !item.adminArchived)}
                          title={item.adminArchived ? "Restore to main list" : "Archive (hide from default list)"}
                        >
                          {item.adminArchived ? "Restore" : "Archive"}
                        </Button>
                      </DataTableRow>
                    </DataTableItem>
                  ))
                )}
              </DataTableBody>
            </DataTable>
          )}

          {!loading && totalItems > itemPerPage && (
            <div className="card-inner border-top">
              <PaginationComponent
                itemPerPage={itemPerPage}
                totalItems={totalItems}
                currentPage={currentPage}
                paginate={setCurrentPage}
              />
            </div>
          )}
        </Block>
      </TeleconsultationPageShell>
    </>
  );
};

export default TeleconsultationList;
